import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { getVehicleById, updateVehicle, addVehicle, getAllCategories, getAllBrands } from "@/services/vehicleService";
import type { VehicleCategory, Brand, VehicleSpecifications } from "@oriental-ramirez/shared";
import { VehicleCategoryType } from "@oriental-ramirez/shared";
import { toast } from "sonner";
import { ImageUploadField } from "@/components/ImageUploadField";
import {
  CONDITION_OPTIONS,
  ENGINE_TYPE_OPTIONS,
  TRANSMISSION_OPTIONS,
  CAR_BODY_OPTIONS,
  DRIVE_TYPE_OPTIONS,
  MOTORCYCLE_TYPE_OPTIONS,
  STARTER_TYPE_OPTIONS,
  COOLING_OPTIONS,
} from "@/constants/vehicleOptions";
import { Info, Settings, Film, FileText } from "lucide-react";
import { queryKeys } from "@/lib/queryKeys";
import { useQuery } from "@tanstack/react-query";

const schema = z.object({
  name: z.string().min(1, "Requerido"),
  model: z.string().optional(),
  year: z.union([z.coerce.number(), z.nan()]).optional(),
  km: z.union([z.coerce.number(), z.nan()]).optional(),
  categoryId: z.string().min(1, "Selecciona categoría"),
  brandId: z.string().min(1, "Selecciona marca"),
  salePrice: z.union([z.coerce.number(), z.nan()]).optional(),
  minPrice: z.union([z.coerce.number(), z.nan()]).optional(),
  initPrice: z.union([z.coerce.number(), z.nan()]).optional(),
  dealerPrice: z.union([z.coerce.number(), z.nan()]).optional(),
  currency: z.enum(["USD", "DOP"]).optional(),
  description: z.string().optional(),
  condition: z.string().optional(),
  tags: z.string().optional(),
  warranty: z.string().optional(),
  videos: z.string().optional(),
  // Motorcycle spec
  motorcycleType: z.string().optional(),
  cc: z.string().optional(),
  engineType: z.string().optional(),
  cylinders: z.string().optional(),
  cooling: z.string().optional(),
  transmission: z.string().optional(),
  gears: z.string().optional(),
  starter: z.string().optional(),
  horsePower: z.string().optional(),
  torque: z.string().optional(),
  maxSpeed: z.string().optional(),
  fuelTank: z.string().optional(),
  fuelConsumption: z.string().optional(),
  frontSuspension: z.string().optional(),
  rearSuspension: z.string().optional(),
  frontBrake: z.string().optional(),
  rearBrake: z.string().optional(),
  abs: z.boolean().optional(),
  length: z.string().optional(),
  width: z.string().optional(),
  height: z.string().optional(),
  seatHeight: z.string().optional(),
  wheelbase: z.string().optional(),
  weight: z.string().optional(),
  digitalPanel: z.boolean().optional(),
  usbCharger: z.boolean().optional(),
  tractionControl: z.boolean().optional(),
  quickshifter: z.boolean().optional(),
  ridingModes: z.boolean().optional(),
  // Automobile spec
  bodyType: z.string().optional(),
  doors: z.string().optional(),
  seats: z.string().optional(),
  driveType: z.string().optional(),
  engineSize: z.string().optional(),
  horsepower: z.string().optional(),
  cityConsumption: z.string().optional(),
  highwayConsumption: z.string().optional(),
  cargoCapacity: z.string().optional(),
  airbags: z.string().optional(),
  stabilityControl: z.boolean().optional(),
  parkingSensors: z.boolean().optional(),
  reverseCamera: z.boolean().optional(),
  blindSpotAlert: z.boolean().optional(),
  laneAssist: z.boolean().optional(),
  collisionAlert: z.boolean().optional(),
  airConditioning: z.string().optional(),
  sunroof: z.boolean().optional(),
  leatherSeats: z.boolean().optional(),
  heatedSeats: z.boolean().optional(),
  electricSeats: z.boolean().optional(),
  touchScreen: z.string().optional(),
  navigation: z.boolean().optional(),
  bluetooth: z.boolean().optional(),
  usbPorts: z.string().optional(),
  soundSystem: z.string().optional(),
  appleCarPlay: z.boolean().optional(),
  androidAuto: z.boolean().optional(),
});

type FormData = z.infer<typeof schema>;

function parseNum(v: unknown): number | undefined {
  if (v === undefined || v === null || v === "") return undefined;
  const n = Number(v);
  return !isNaN(n) && n > 0 ? n : undefined;
}

function toTags(s?: string): string[] | undefined {
  if (!s?.trim()) return undefined;
  return s.split(",").map((t) => t.trim()).filter(Boolean);
}

function toVideoUrls(s?: string): string[] | undefined {
  if (!s?.trim()) return undefined;
  const urls = s.split(/\r?\n/).map((u) => u.trim()).filter(Boolean);
  const valid = urls.filter((url) => /youtube\.com|youtu\.be/i.test(url));
  return valid.length ? valid : undefined;
}

function buildSpecifications(data: FormData, typeParam: string | null): VehicleSpecifications | undefined {
  const isMoto = typeParam === "motorcycle";
  const spec: Record<string, unknown> = {};

  if (isMoto) {
    if (data.motorcycleType) spec.motorcycleType = data.motorcycleType;
    if (data.cc) spec.cc = data.cc;
    if (data.engineType) spec.engineType = data.engineType;
    if (data.cylinders) spec.cylinders = parseNum(data.cylinders);
    if (data.cooling) spec.cooling = data.cooling;
    if (data.transmission) spec.transmission = data.transmission;
    if (data.gears) spec.gears = data.gears;
    if (data.starter) spec.starter = data.starter;
    if (data.horsePower) spec.horsepower = `${data.horsePower} HP`;
    if (data.torque) spec.torque = `${data.torque} Nm`;
    if (data.maxSpeed) spec.maxSpeed = `${data.maxSpeed} km/h`;
    if (data.fuelTank) spec.fuelTank = `${data.fuelTank} L`;
    if (data.fuelConsumption) spec.fuelConsumption = `${data.fuelConsumption} km/L`;
    if (data.frontSuspension) spec.frontSuspension = data.frontSuspension;
    if (data.rearSuspension) spec.rearSuspension = data.rearSuspension;
    if (data.frontBrake) spec.frontBrake = data.frontBrake;
    if (data.rearBrake) spec.rearBrake = data.rearBrake;
    spec.abs = data.abs ?? false;
    if (data.length) spec.length = `${data.length} mm`;
    if (data.width) spec.width = `${data.width} mm`;
    if (data.height) spec.height = `${data.height} mm`;
    if (data.seatHeight) spec.seatHeight = `${data.seatHeight} mm`;
    if (data.wheelbase) spec.wheelbase = `${data.wheelbase} mm`;
    if (data.weight) spec.weight = `${data.weight} kg`;
    spec.digitalPanel = data.digitalPanel ?? false;
    spec.usbCharger = data.usbCharger ?? false;
    spec.tractionControl = data.tractionControl ?? false;
    spec.quickshifter = data.quickshifter ?? false;
    spec.ridingModes = data.ridingModes ?? false;
  } else {
    if (data.bodyType) spec.bodyType = data.bodyType;
    if (data.doors) spec.doors = data.doors;
    if (data.seats) spec.seats = data.seats;
    if (data.driveType) spec.driveType = data.driveType;
    if (data.engineType) spec.engineType = data.engineType;
    if (data.engineSize) spec.engineSize = `${data.engineSize} L`;
    if (data.cylinders) spec.cylinders = data.cylinders;
    if (data.horsepower) spec.horsepower = `${data.horsepower} HP`;
    if (data.torque) spec.torque = `${data.torque} Nm`;
    if (data.transmission) spec.transmission = data.transmission;
    if (data.gears) spec.gears = data.gears;
    if (data.fuelTank) spec.fuelTank = `${data.fuelTank} L`;
    if (data.cityConsumption) spec.cityConsumption = `${data.cityConsumption} km/L`;
    if (data.highwayConsumption) spec.highwayConsumption = `${data.highwayConsumption} km/L`;
    if (data.length) spec.length = `${data.length} mm`;
    if (data.width) spec.width = `${data.width} mm`;
    if (data.height) spec.height = `${data.height} mm`;
    if (data.wheelbase) spec.wheelbase = `${data.wheelbase} mm`;
    if (data.weight) spec.weight = `${data.weight} kg`;
    if (data.cargoCapacity) spec.cargoCapacity = `${data.cargoCapacity} L`;
    if (data.airbags) spec.airbags = data.airbags;
    spec.abs = data.abs ?? false;
    spec.stabilityControl = data.stabilityControl ?? false;
    spec.tractionControl = data.tractionControl ?? false;
    spec.parkingSensors = data.parkingSensors ?? false;
    spec.reverseCamera = data.reverseCamera ?? false;
    spec.blindSpotAlert = data.blindSpotAlert ?? false;
    spec.laneAssist = data.laneAssist ?? false;
    spec.collisionAlert = data.collisionAlert ?? false;
    if (data.airConditioning) spec.airConditioning = data.airConditioning;
    spec.sunroof = data.sunroof ?? false;
    spec.leatherSeats = data.leatherSeats ?? false;
    spec.heatedSeats = data.heatedSeats ?? false;
    spec.electricSeats = data.electricSeats ?? false;
    if (data.touchScreen) spec.touchScreen = data.touchScreen;
    spec.navigation = data.navigation ?? false;
    spec.bluetooth = data.bluetooth ?? false;
    if (data.usbPorts) spec.usbPorts = data.usbPorts;
    if (data.soundSystem) spec.soundSystem = data.soundSystem;
    spec.appleCarPlay = data.appleCarPlay ?? false;
    spec.androidAuto = data.androidAuto ?? false;
  }

  return Object.keys(spec).length ? (spec as VehicleSpecifications) : undefined;
}

export function VehicleForm() {
  const queryClient = useQueryClient();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const typeParam = searchParams.get("type");
  const defaultVehicleType =
    typeParam === "automobile" ? VehicleCategoryType.AUTOMOBILE : VehicleCategoryType.MOTORCYCLE;
  const { data: categories = [] } = useQuery({
    queryKey: queryKeys.vehicleCategories,
    queryFn: getAllCategories,
  });
  const { data: brands = [] } = useQuery({
    queryKey: queryKeys.vehicleBrands,
    queryFn: getAllBrands,
  });
  const [loading, setLoading] = useState(isEdit);
  const [images, setImages] = useState<string[]>([]);

  const categoriesFiltered = useMemo(() => {
    if (!typeParam) return categories;
    const wantMoto = typeParam === "motorcycle";
    return categories.filter((c) => {
      const t = (c.vehicleType ?? c.type) as string | undefined;
      const isMoto =
        t === VehicleCategoryType.MOTORCYCLE || t === "Motocicleta" || !t;
      return wantMoto ? isMoto : !isMoto;
    });
  }, [categories, typeParam]);

  const defaultSpec = (): Partial<FormData> => ({
    condition: "",
    tags: "",
    warranty: "",
    videos: "",
    motorcycleType: "",
    cc: "",
    engineType: "",
    cylinders: "",
    cooling: "",
    transmission: "",
    gears: "",
    starter: "",
    horsePower: "",
    torque: "",
    maxSpeed: "",
    fuelTank: "",
    fuelConsumption: "",
    frontSuspension: "",
    rearSuspension: "",
    frontBrake: "",
    rearBrake: "",
    abs: false,
    length: "",
    width: "",
    height: "",
    seatHeight: "",
    wheelbase: "",
    weight: "",
    digitalPanel: false,
    usbCharger: false,
    tractionControl: false,
    quickshifter: false,
    ridingModes: false,
    bodyType: "",
    doors: "",
    seats: "",
    driveType: "",
    engineSize: "",
    horsepower: "",
    cityConsumption: "",
    highwayConsumption: "",
    cargoCapacity: "",
    airbags: "",
    stabilityControl: false,
    parkingSensors: false,
    reverseCamera: false,
    blindSpotAlert: false,
    laneAssist: false,
    collisionAlert: false,
    airConditioning: "",
    sunroof: false,
    leatherSeats: false,
    heatedSeats: false,
    electricSeats: false,
    touchScreen: "",
    navigation: false,
    bluetooth: false,
    usbPorts: "",
    soundSystem: "",
    appleCarPlay: false,
    androidAuto: false,
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      currency: "USD",
      ...defaultSpec(),
    },
  });

  const categoryId = watch("categoryId");
  const brandId = watch("brandId");
  const isMotorcycle = typeParam === "motorcycle";

  useEffect(() => {
    if (!id) return;
    getVehicleById(id).then((v) => {
      if (v) {
        setValue("name", v.name ?? "");
        setValue("model", v.model ?? "");
        setValue("year", v.year ?? undefined);
        setValue("km", v.km ?? undefined);
        setValue("categoryId", (v.category as VehicleCategory)?.id ?? (typeof v.category === "string" ? v.category : ""));
        setValue("brandId", (v.brand as Brand)?.id ?? (typeof v.brand === "string" ? v.brand : ""));
        setValue("salePrice", v.salePrice ?? undefined);
        setValue("minPrice", v.minPrice ?? undefined);
        setValue("initPrice", v.initPrice ?? undefined);
        setValue("dealerPrice", v.dealerPrice ?? undefined);
        setValue("currency", (v.currency as "USD" | "DOP") ?? "USD");
        setValue("description", v.description ?? "");
        setValue("condition", (v.condition as string) ?? "");
        setValue("warranty", v.warranty ?? "");
        setValue("tags", Array.isArray(v.tags) ? v.tags.join(", ") : ((v.tags as string | undefined) ?? ""));
        setValue("videos", Array.isArray(v.videos) ? v.videos.join("\n") : ((v.videos as string | undefined) ?? ""));
        setImages(v.images ?? []);

        const s = v.specifications;
        if (s) {
          if (s.motorcycleType) setValue("motorcycleType", s.motorcycleType);
          if (s.cc) setValue("cc", String(s.cc));
          if (s.engineType) setValue("engineType", s.engineType as string);
          if (s.cylinders) setValue("cylinders", String(s.cylinders));
          if (s.cooling) setValue("cooling", s.cooling);
          if (s.transmission) setValue("transmission", s.transmission as string);
          if (s.gears) setValue("gears", String(s.gears));
          if (s.starter) setValue("starter", s.starter);
          if (s.horsepower) setValue("horsePower", String(s.horsepower).replace(/\s*HP$/i, ""));
          if (s.torque) setValue("torque", String(s.torque).replace(/\s*Nm$/i, ""));
          if (s.maxSpeed) setValue("maxSpeed", String(s.maxSpeed).replace(/\s*km\/h$/i, ""));
          if (s.fuelTank) setValue("fuelTank", String(s.fuelTank).replace(/\s*(L|litros)$/i, ""));
          if (s.fuelConsumption) setValue("fuelConsumption", String(s.fuelConsumption).replace(/\s*km\/L$/i, ""));
          if (s.frontSuspension) setValue("frontSuspension", s.frontSuspension);
          if (s.rearSuspension) setValue("rearSuspension", s.rearSuspension);
          if (s.frontBrake) setValue("frontBrake", s.frontBrake);
          if (s.rearBrake) setValue("rearBrake", s.rearBrake);
          if (typeof s.abs === "boolean") setValue("abs", s.abs);
          if (s.length) setValue("length", String(s.length).replace(/\s*mm$/i, ""));
          if (s.width) setValue("width", String(s.width).replace(/\s*mm$/i, ""));
          if (s.height) setValue("height", String(s.height).replace(/\s*mm$/i, ""));
          if (s.seatHeight) setValue("seatHeight", String(s.seatHeight).replace(/\s*mm$/i, ""));
          if (s.wheelbase) setValue("wheelbase", String(s.wheelbase).replace(/\s*mm$/i, ""));
          if (s.weight) setValue("weight", String(s.weight).replace(/\s*kg$/i, ""));
          if (typeof s.digitalPanel === "boolean") setValue("digitalPanel", s.digitalPanel);
          if (typeof s.usbCharger === "boolean") setValue("usbCharger", s.usbCharger);
          if (typeof s.tractionControl === "boolean") setValue("tractionControl", s.tractionControl);
          if (typeof s.quickshifter === "boolean") setValue("quickshifter", s.quickshifter);
          if (typeof s.ridingModes === "boolean") setValue("ridingModes", s.ridingModes);
          if (s.bodyType) setValue("bodyType", s.bodyType);
          if (s.doors) setValue("doors", String(s.doors));
          if (s.seats) setValue("seats", String(s.seats));
          if (s.driveType) setValue("driveType", s.driveType as string);
          if (s.engineSize) setValue("engineSize", String(s.engineSize).replace(/\s*L$/i, ""));
          if (s.cityConsumption) setValue("cityConsumption", String(s.cityConsumption).replace(/\s*km\/L$/i, ""));
          if (s.highwayConsumption) setValue("highwayConsumption", String(s.highwayConsumption).replace(/\s*km\/L$/i, ""));
          if (s.cargoCapacity) setValue("cargoCapacity", String(s.cargoCapacity).replace(/\s*L$/i, ""));
          if (s.airbags) setValue("airbags", String(s.airbags));
          if (typeof s.stabilityControl === "boolean") setValue("stabilityControl", s.stabilityControl);
          if (typeof s.parkingSensors === "boolean") setValue("parkingSensors", s.parkingSensors);
          if (typeof s.reverseCamera === "boolean") setValue("reverseCamera", s.reverseCamera);
          if (typeof s.blindSpotAlert === "boolean") setValue("blindSpotAlert", s.blindSpotAlert);
          if (typeof s.laneAssist === "boolean") setValue("laneAssist", s.laneAssist);
          if (typeof s.collisionAlert === "boolean") setValue("collisionAlert", s.collisionAlert);
          if (s.airConditioning != null) setValue("airConditioning", String(s.airConditioning));
          if (typeof s.sunroof === "boolean") setValue("sunroof", s.sunroof);
          if (typeof s.leatherSeats === "boolean") setValue("leatherSeats", s.leatherSeats);
          if (typeof s.heatedSeats === "boolean") setValue("heatedSeats", s.heatedSeats);
          if (typeof s.electricSeats === "boolean") setValue("electricSeats", s.electricSeats);
          if (s.touchScreen) setValue("touchScreen", String(s.touchScreen));
          if (typeof s.navigation === "boolean") setValue("navigation", s.navigation);
          if (typeof s.bluetooth === "boolean") setValue("bluetooth", s.bluetooth);
          if (s.usbPorts) setValue("usbPorts", String(s.usbPorts));
          if (s.soundSystem) setValue("soundSystem", String(s.soundSystem));
          if (typeof s.appleCarPlay === "boolean") setValue("appleCarPlay", s.appleCarPlay);
          if (typeof s.androidAuto === "boolean") setValue("androidAuto", s.androidAuto);
        }
      }
      setLoading(false);
    });
  }, [id, setValue]);

  const onSubmit = async (data: FormData) => {
    try {
      const category = categories.find((c) => c.id === data.categoryId);
      const brand = brands.find((b) => b.id === data.brandId);
      const specifications = buildSpecifications(data, typeParam);
      const payload = {
        name: data.name,
        model: data.model,
        year: parseNum(data.year),
        km: parseNum(data.km),
        category,
        categoryId: data.categoryId,
        brand,
        brandId: data.brandId,
        salePrice: data.salePrice != null && !isNaN(Number(data.salePrice)) ? Number(data.salePrice) : undefined,
        minPrice: data.minPrice != null && !isNaN(Number(data.minPrice)) ? Number(data.minPrice) : undefined,
        initPrice: data.initPrice != null && !isNaN(Number(data.initPrice)) ? Number(data.initPrice) : undefined,
        dealerPrice: data.dealerPrice != null && !isNaN(Number(data.dealerPrice)) ? Number(data.dealerPrice) : undefined,
        currency: data.currency ?? "USD",
        description: data.description,
        condition: data.condition ? (data.condition as unknown as import("@oriental-ramirez/shared").VehicleCondition) : undefined,
        type: category?.vehicleType ?? category?.type ?? defaultVehicleType,
        images,
        specifications,
        warranty: data.warranty || undefined,
        tags: toTags(data.tags),
        videos: toVideoUrls(data.videos),
      };
      if (isEdit && id) {
        await updateVehicle(id, payload);
        toast.success("Vehículo actualizado");
      } else {
        await addVehicle(payload as Parameters<typeof addVehicle>[0]);
        toast.success("Vehículo creado");
      }
      await queryClient.invalidateQueries({ queryKey: queryKeys.vehicles });
      navigate("/vehicles");
    } catch {
      toast.error("Error al guardar");
    }
  };

  if (isEdit && loading) return <p className="text-muted-foreground">Cargando…</p>;

  return (
    <div className="max-w-5xl space-y-6">
      <h1 className="font-display text-2xl font-semibold">{isEdit ? "Editar vehículo" : typeParam === "motorcycle" ? "Nueva motocicleta" : "Nuevo automóvil"}</h1>
      <div className="flex flex-col gap-6 lg:flex-row">
        <Card className="min-w-0 flex-1">
          <CardHeader>
            <CardTitle>Datos del vehículo</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <Tabs defaultValue="general" className="w-full">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="general" className="gap-1.5">
                    <Info className="h-4 w-4" />
                    General
                  </TabsTrigger>
                  <TabsTrigger value="details" className="gap-1.5">
                    <FileText className="h-4 w-4" />
                    Detalles
                  </TabsTrigger>
                  <TabsTrigger value="technical" className="gap-1.5">
                    <Settings className="h-4 w-4" />
                    Técnico
                  </TabsTrigger>
                  <TabsTrigger value="multimedia" className="gap-1.5">
                    <Film className="h-4 w-4" />
                    Multimedia
                  </TabsTrigger>
                </TabsList>

                {/* Tab General: solo campos importantes */}
                <TabsContent value="general" className="space-y-4 pt-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Nombre / Modelo *</Label>
                      <Input {...register("name")} placeholder="Ej. Honda CB600F Hornet 2024" />
                      {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
                    </div>
                    {!isMotorcycle && (
                      <div className="space-y-2">
                        <Label>Modelo (auto)</Label>
                        <Input {...register("model")} placeholder="Ej. Civic" />
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Descripción</Label>
                    <Textarea {...register("description")} placeholder="Opcional" rows={3} />
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Año</Label>
                      <Input type="number" {...register("year")} placeholder="2024" />
                    </div>
                    <div className="space-y-2">
                      <Label>Kilometraje</Label>
                      <Input type="number" {...register("km")} placeholder="0" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Condición</Label>
                    <Select value={watch("condition") || "none"} onValueChange={(v) => setValue("condition", v === "none" ? "" : v)}>
                      <SelectTrigger><SelectValue placeholder="Opcional" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">—</SelectItem>
                        {CONDITION_OPTIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Categoría *</Label>
                      <Select value={categoryId || undefined} onValueChange={(v) => setValue("categoryId", v)}>
                        <SelectTrigger><SelectValue placeholder="Seleccionar categoría" /></SelectTrigger>
                        <SelectContent>
                          {categoriesFiltered.filter((c) => c.id).map((c) => (
                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.categoryId && <p className="text-sm text-destructive">{errors.categoryId.message}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label>Marca *</Label>
                      <Select value={brandId || undefined} onValueChange={(v) => setValue("brandId", v)}>
                        <SelectTrigger><SelectValue placeholder="Seleccionar marca" /></SelectTrigger>
                        <SelectContent>
                          {brands.filter((b) => b.id).map((b) => (
                            <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.brandId && <p className="text-sm text-destructive">{errors.brandId.message}</p>}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Moneda</Label>
                    <Select value={watch("currency") ?? "USD"} onValueChange={(v) => setValue("currency", v as "USD" | "DOP")}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="DOP">DOP</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Precio de venta</Label>
                      <Input type="number" step="0.01" {...register("salePrice")} />
                    </div>
                    <div className="space-y-2">
                      <Label>Precio inicial (mín. entrada)</Label>
                      <Input type="number" step="0.01" {...register("initPrice")} />
                    </div>
                  </div>
                </TabsContent>

                {/* Tab Detalles: precios secundarios, garantía, tags */}
                <TabsContent value="details" className="space-y-4 pt-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Precio mínimo</Label>
                      <Input type="number" step="0.01" {...register("minPrice")} placeholder="Opcional" />
                    </div>
                    <div className="space-y-2">
                      <Label>Precio sugerido (dealer)</Label>
                      <Input type="number" step="0.01" {...register("dealerPrice")} placeholder="Uso interno" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Garantía</Label>
                    <Input {...register("warranty")} placeholder="Ej. 1 año o 20,000 km" />
                  </div>
                  <div className="space-y-2">
                    <Label>Tags (separados por coma)</Label>
                    <Input {...register("tags")} placeholder="deportiva, urbana, económica" />
                  </div>
                </TabsContent>

                <TabsContent value="technical" className="space-y-4 pt-4">
                  {isMotorcycle ? (
                    <>
                      <div className="space-y-2">
                        <Label>Tipo de motocicleta</Label>
                        <Select value={watch("motorcycleType") || "none"} onValueChange={(v) => setValue("motorcycleType", v === "none" ? "" : v)}>
                          <SelectTrigger><SelectValue placeholder="Opcional" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">—</SelectItem>
                            {MOTORCYCLE_TYPE_OPTIONS.map((o) => (
                              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label>Cilindrada (CC)</Label>
                          <Input type="number" {...register("cc")} placeholder="600" />
                        </div>
                        <div className="space-y-2">
                          <Label>Cilindros</Label>
                          <Input {...register("cylinders")} placeholder="4" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Tipo de motor</Label>
                        <Select value={watch("engineType") || "none"} onValueChange={(v) => setValue("engineType", v === "none" ? "" : v)}>
                          <SelectTrigger><SelectValue placeholder="Opcional" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">—</SelectItem>
                            {ENGINE_TYPE_OPTIONS.map((o) => (
                              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Refrigeración</Label>
                        <Select value={watch("cooling") || "none"} onValueChange={(v) => setValue("cooling", v === "none" ? "" : v)}>
                          <SelectTrigger><SelectValue placeholder="Opcional" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">—</SelectItem>
                            {COOLING_OPTIONS.map((c) => (
                              <SelectItem key={c} value={c}>{c}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label>Transmisión</Label>
                          <Select value={watch("transmission") || "none"} onValueChange={(v) => setValue("transmission", v === "none" ? "" : v)}>
                            <SelectTrigger><SelectValue placeholder="Opcional" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">—</SelectItem>
                              {TRANSMISSION_OPTIONS.map((o) => (
                                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Velocidades</Label>
                          <Input {...register("gears")} placeholder="6" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Sistema de arranque</Label>
                        <Select value={watch("starter") || "none"} onValueChange={(v) => setValue("starter", v === "none" ? "" : v)}>
                          <SelectTrigger><SelectValue placeholder="Opcional" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">—</SelectItem>
                            {STARTER_TYPE_OPTIONS.map((o) => (
                              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label>Potencia (HP)</Label>
                          <Input {...register("horsePower")} placeholder="90" />
                        </div>
                        <div className="space-y-2">
                          <Label>Torque (Nm)</Label>
                          <Input {...register("torque")} placeholder="65" />
                        </div>
                      </div>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label>Vel. máxima (km/h)</Label>
                          <Input {...register("maxSpeed")} placeholder="220" />
                        </div>
                        <div className="space-y-2">
                          <Label>Tanque (L)</Label>
                          <Input {...register("fuelTank")} placeholder="15" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Consumo (km/L)</Label>
                        <Input {...register("fuelConsumption")} placeholder="35" />
                      </div>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label>Susp. delantera</Label>
                          <Input {...register("frontSuspension")} placeholder="Horquilla telescópica" />
                        </div>
                        <div className="space-y-2">
                          <Label>Susp. trasera</Label>
                          <Input {...register("rearSuspension")} placeholder="Monoamortiguador" />
                        </div>
                      </div>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label>Freno delantero</Label>
                          <Input {...register("frontBrake")} placeholder="Disco 320mm" />
                        </div>
                        <div className="space-y-2">
                          <Label>Freno trasero</Label>
                          <Input {...register("rearBrake")} placeholder="Disco 240mm" />
                        </div>
                      </div>
                      <div className="flex items-center justify-between rounded-lg border p-4">
                        <Label>ABS</Label>
                        <Switch checked={watch("abs")} onCheckedChange={(v) => setValue("abs", v)} />
                      </div>
                      <div className="grid gap-4 sm:grid-cols-2">
                        {(["length", "width", "height", "seatHeight", "wheelbase", "weight"] as const).map((key) => (
                          <div key={key} className="space-y-2">
                            <Label>{key === "length" ? "Largo (mm)" : key === "width" ? "Ancho (mm)" : key === "height" ? "Alto (mm)" : key === "seatHeight" ? "Altura asiento (mm)" : key === "wheelbase" ? "Dist. ejes (mm)" : "Peso (kg)"}</Label>
                            <Input type="number" {...register(key)} />
                          </div>
                        ))}
                      </div>
                      <div className="flex flex-wrap gap-4">
                        {(["digitalPanel", "usbCharger", "tractionControl", "quickshifter", "ridingModes"] as const).map((key) => (
                          <div key={key} className="flex items-center gap-2">
                            <Switch checked={watch(key)} onCheckedChange={(v) => setValue(key, v)} />
                            <Label className="capitalize">{key.replace(/([A-Z])/g, " $1").trim()}</Label>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label>Carrocería</Label>
                          <Select value={watch("bodyType") || "none"} onValueChange={(v) => setValue("bodyType", v === "none" ? "" : v)}>
                            <SelectTrigger><SelectValue placeholder="Opcional" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">—</SelectItem>
                              {CAR_BODY_OPTIONS.map((o) => (
                                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Tracción</Label>
                          <Select value={watch("driveType") || "none"} onValueChange={(v) => setValue("driveType", v === "none" ? "" : v)}>
                            <SelectTrigger><SelectValue placeholder="Opcional" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">—</SelectItem>
                              {DRIVE_TYPE_OPTIONS.map((o) => (
                                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label>Puertas</Label>
                          <Input {...register("doors")} placeholder="4" />
                        </div>
                        <div className="space-y-2">
                          <Label>Asientos</Label>
                          <Input {...register("seats")} placeholder="5" />
                        </div>
                      </div>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label>Tipo de motor</Label>
                          <Select value={watch("engineType") || "none"} onValueChange={(v) => setValue("engineType", v === "none" ? "" : v)}>
                            <SelectTrigger><SelectValue placeholder="Opcional" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">—</SelectItem>
                              {ENGINE_TYPE_OPTIONS.map((o) => (
                                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Cilindrada (L)</Label>
                          <Input {...register("engineSize")} placeholder="2.0" />
                        </div>
                      </div>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label>Cilindros</Label>
                          <Input {...register("cylinders")} placeholder="4" />
                        </div>
                        <div className="space-y-2">
                          <Label>Transmisión</Label>
                          <Select value={watch("transmission") || "none"} onValueChange={(v) => setValue("transmission", v === "none" ? "" : v)}>
                            <SelectTrigger><SelectValue placeholder="Opcional" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">—</SelectItem>
                              {TRANSMISSION_OPTIONS.map((o) => (
                                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label>Potencia (HP)</Label>
                          <Input {...register("horsepower")} placeholder="150" />
                        </div>
                        <div className="space-y-2">
                          <Label>Torque (Nm)</Label>
                          <Input {...register("torque")} placeholder="200" />
                        </div>
                      </div>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label>Tanque (L)</Label>
                          <Input {...register("fuelTank")} placeholder="50" />
                        </div>
                        <div className="space-y-2">
                          <Label>Consumo ciudad (km/L)</Label>
                          <Input {...register("cityConsumption")} placeholder="12" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Consumo carretera (km/L)</Label>
                        <Input {...register("highwayConsumption")} placeholder="16" />
                      </div>
                      <div className="grid gap-4 sm:grid-cols-2">
                        {(["length", "width", "height", "wheelbase", "weight", "cargoCapacity"] as const).map((key) => (
                          <div key={key} className="space-y-2">
                            <Label>{key === "length" ? "Largo (mm)" : key === "width" ? "Ancho (mm)" : key === "height" ? "Alto (mm)" : key === "wheelbase" ? "Dist. ejes (mm)" : key === "weight" ? "Peso (kg)" : "Maletero (L)"}</Label>
                            <Input type="number" {...register(key)} />
                          </div>
                        ))}
                      </div>
                      <div className="space-y-2">
                        <Label>Airbags</Label>
                        <Input {...register("airbags")} placeholder="6" />
                      </div>
                      <div className="flex flex-wrap gap-4">
                        {(["abs", "stabilityControl", "tractionControl", "parkingSensors", "reverseCamera", "blindSpotAlert", "laneAssist", "collisionAlert"] as const).map((key) => (
                          <div key={key} className="flex items-center gap-2">
                            <Switch checked={watch(key)} onCheckedChange={(v) => setValue(key, v)} />
                            <Label className="capitalize">{key.replace(/([A-Z])/g, " $1").trim()}</Label>
                          </div>
                        ))}
                      </div>
                      <div className="space-y-2">
                        <Label>Aire acondicionado</Label>
                        <Input {...register("airConditioning")} placeholder="Climatizado dual" />
                      </div>
                      <div className="flex flex-wrap gap-4">
                        {(["sunroof", "leatherSeats", "heatedSeats", "electricSeats"] as const).map((key) => (
                          <div key={key} className="flex items-center gap-2">
                            <Switch checked={watch(key)} onCheckedChange={(v) => setValue(key, v)} />
                            <Label className="capitalize">{key.replace(/([A-Z])/g, " $1").trim()}</Label>
                          </div>
                        ))}
                      </div>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label>Pantalla táctil</Label>
                          <Input {...register("touchScreen")} placeholder='Ej. 8"' />
                        </div>
                        <div className="space-y-2">
                          <Label>Puertos USB</Label>
                          <Input {...register("usbPorts")} placeholder="2" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Sistema de sonido</Label>
                        <Input {...register("soundSystem")} placeholder="Ej. Bose" />
                      </div>
                      <div className="flex flex-wrap gap-4">
                        {(["navigation", "bluetooth", "appleCarPlay", "androidAuto"] as const).map((key) => (
                          <div key={key} className="flex items-center gap-2">
                            <Switch checked={watch(key)} onCheckedChange={(v) => setValue(key, v)} />
                            <Label className="capitalize">{key.replace(/([A-Z])/g, " $1").trim()}</Label>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </TabsContent>

                <TabsContent value="multimedia" className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label>URLs de videos (YouTube, una por línea)</Label>
                    <Textarea {...register("videos")} placeholder="https://youtube.com/watch?v=..." rows={4} />
                  </div>
                </TabsContent>
              </Tabs>

              <div className="mt-4 flex gap-2">
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Guardando…" : "Guardar"}
                </Button>
                <Button type="button" variant="outline" onClick={() => navigate("/vehicles")}>
                  Cancelar
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
        <aside className="w-full shrink-0 lg:sticky lg:top-6 lg:w-80 lg:self-start">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Fotos del vehículo</CardTitle>
            </CardHeader>
            <CardContent>
              <ImageUploadField
                value={images}
                onChange={setImages}
                folder={typeParam === "motorcycle" ? "motorcycles" : "automobiles"}
                label=""
              />
            </CardContent>
          </Card>
        </aside>
      </div>
    </div>
  );
}
